import * as Howl from "howler";
import * as Howler from "howler";
import { Signal } from "signals";
import { SoundConfigSchema } from "slotworks/config/sound-config-schema";
import Loader from "slotworks/loader/loader";
import { SoundEvent } from "slotworks/sound/sound-events";
import { parseJSONMap } from "slotworks/utils/json-utils";
import DeviceInfo from "slotworks/utils/device-info";
import ls from "slotworks/utils/local-storage";

class SoundManager {

    public onMuteChange: Signal;
    public onLoadComplete: Signal;

    private soundAliases: Map<string, string>;

    private eventStartSounds: Map<string, SoundPlayConfig[]>;
    private eventStopSounds: Map<string, SoundStopConfig[]>;
    private terminators: Map<string, string>;

    private playingSounds: Map<string, PlayingSound[]> = new Map<string, PlayingSound[]>();
    private pausedSounds: Map<string, number> = new Map<string, number>();

    private soundSprite: Howl;

    private muted: boolean = true;
    private musicMuted: boolean = false;
    private soundFxMuted: boolean = false;
    private musicQueue: string[] = [];

    private volume: number = 1;

    private isLoading: boolean = false;
    private loaded: boolean = false;

    private soundsWaitingForLoad: Map<string, SoundPlayConfig> = new Map<string, SoundPlayConfig>();

    constructor() {
        this.onMuteChange = new Signal();
        this.onLoadComplete = new Signal();

        this.loadSettings();
    }

    public setSoundSprite(sound: Howl) {
        this.soundSprite = sound;

        this.soundsWaitingForLoad.forEach((soundPlayConfig: SoundPlayConfig) => {
            this.playSound(soundPlayConfig);
        });
        this.soundsWaitingForLoad.clear();

        this.isLoading = false;
        this.loaded = true;

        this.onLoadComplete.dispatch();
    }

    public registerSounds(soundConfig: SoundConfigSchema) {

        this.soundAliases = new Map<string, string>();
        this.eventStartSounds = new Map<string, SoundPlayConfig[]>();
        this.eventStopSounds = new Map<string, SoundStopConfig[]>();

        for (const soundAlias of Object.keys(soundConfig.aliases)) {
            this.soundAliases.set(soundAlias, soundConfig.aliases[soundAlias]);
        }

        for (const event of Object.keys(soundConfig.events)) {
            const playSounds: SoundPlayConfig[] = [];

            if (soundConfig.events[event].play) {
                for (const sound of soundConfig.events[event].play) {
                    let playSound;
                    if (typeof sound === "string") {
                        playSound = new SoundPlayConfig(sound, false, 0, false);
                    } else {
                        playSound = new SoundPlayConfig(sound.id, sound.loop, sound.fadeTime, sound.singleton, sound.music, sound.volume);
                    }

                    playSounds.push(playSound);

                    if (!playSound.music && !this.soundAliases.has(playSound.id)) {
                        throw new Error("Play sound not found " + playSound.id + " for " + event);
                    }
                }
            }

            this.eventStartSounds.set(event, playSounds);

            const stopSounds: SoundStopConfig[] = [];

            if (soundConfig.events[event].stop) {
                for (const sound of soundConfig.events[event].stop) {
                    let stopSound;

                    if (typeof sound === "string") {
                        stopSound = new SoundStopConfig(sound, 0, false);
                    } else {
                        stopSound = new SoundStopConfig(sound.id, sound.fadeTime, sound.pause);
                    }

                    stopSounds.push(stopSound);

                    if (!this.soundAliases.has(stopSound.id)) {
                        throw new Error("Stop sound not found " + stopSound.id + " for " + event);
                    }
                }
            }

            this.eventStopSounds.set(event, stopSounds);
        }

        this.terminators = parseJSONMap<string>(soundConfig.terminators);
    }

    public customEvent(event: string) {

        const soundsToPlay = this.eventStartSounds.get(event);
        const soundsToStop = this.eventStopSounds.get(event);

        if (soundsToPlay) {
            for (const soundPlayConfig of soundsToPlay) {
                this.playSound(soundPlayConfig);
            }
        } else if (event.slice(0, 13) === ("button_press_")) {
            this.event(SoundEvent.default_button_press);
        }

        if (soundsToStop) {
            for (const soundStopConfig of soundsToStop) {
                if (this.stopSound(soundStopConfig.id, soundStopConfig.fadeTime, soundStopConfig.pause)) {
                    if (this.terminators.get(soundStopConfig.id)) {
                        this.playSound(new SoundPlayConfig(this.terminators.get(soundStopConfig.id), false));
                    }
                }
            }
        }
    }

    public event(event: SoundEvent, ...args: string[]) {
        let id: string = event;

        for (let i = 0; i < args.length; i++) {
            id = id.split("{" + i + "}").join(args[i]);
        }

        this.customEvent(id);
    }

    public setLoading() {
        this.isLoading = true;
    }

    public getLoading() {
        return this.isLoading;
    }

    public getLoaded() {
        return this.loaded;
    }

    public loadSounds() {
        if (!this.loaded && !this.isLoading) {
            Loader.loadSounds().then(() => {
                this.event(SoundEvent.sounds_ready);
            });
        }
    }

    public mute() {
        this.setMute(true);
    }

    public unmute() {
        if (this.soundFxMuted && this.musicMuted) {
            this.soundFxMuted = false;
            this.musicMuted = false;
        }
        this.loadSounds();

        this.setMute(false);
    }

    public setSoundFxMute(mute: boolean) {
        this.soundFxMuted = mute;

        this.updateMute();
        ls.set("soundFxMuted", this.soundFxMuted);
    }

    public setMusicMute(mute: boolean) {
        this.musicMuted = mute;

        this.updateMute();
        ls.set("musicMuted", this.musicMuted);
    }

    public getMuted() {
        return this.muted;
    }

    public getSoundFxMuted() {
        return this.soundFxMuted;
    }

    public getMusicMuted() {
        return this.musicMuted;
    }

    public setVolume(volume: number) {
        this.volume = volume;

        Howler.Howler.volume(volume);

        ls.set("volume", this.volume);
    }

    public getVolume() {
        return this.volume;
    }

    public focus() {
        Howler.Howler.volume(this.volume);
    }

    public unfocus() {
        if (this.loaded) {
            Howler.Howler.volume(0);
        }
    }

    private setMute(muted: boolean) {
        const oldMuted = this.muted;
        this.muted = muted;

        if (oldMuted !== this.muted) {
            this.onMuteChange.dispatch(this.muted);
        }

        ls.set("muted", this.muted);
        this.updateMute();
    }

    private getMutedForSound(soundConfig: SoundPlayConfig) {
        const aliasId = soundConfig.id;

        let muted = this.muted;
        if (!muted) {
            if (soundConfig.music) {

                // Only play last music in list
                if (this.musicQueue.indexOf(aliasId) < this.musicQueue.length - 1) {
                    muted = true;
                }

                if (this.musicMuted) {
                    muted = true;
                }
            } else {
                if (this.soundFxMuted) {
                    muted = true;
                }
            }
        }

        return muted;
    }

    private playSound(soundPlayConfig: SoundPlayConfig): number {
        let aliasId = soundPlayConfig.id;

        if (!aliasId && soundPlayConfig.music) {
            if (!this.musicQueue.length) {
                return;
            }
            aliasId = this.musicQueue[this.musicQueue.length - 1];
        }

        const soundId = this.soundAliases.get(aliasId);

        if (soundPlayConfig.music) {
            if (this.musicQueue.indexOf(aliasId) === -1) {
                this.musicQueue.push(aliasId);
            }
        }

        if (!this.soundSprite) {
            if (soundPlayConfig.loop) {
                this.soundsWaitingForLoad.set(aliasId, soundPlayConfig);
            }
            return;
        }

        if (!this.playingSounds.has(aliasId)) {
            this.playingSounds.set(aliasId, []);
        }

        let howlerId: number;

        let playingSounds = this.playingSounds.get(aliasId);

        try {
            if (this.pausedSounds.has(aliasId)) {
                // Reuse paused sound
                howlerId = this.pausedSounds.get(aliasId);
                this.soundSprite.off("fade", this.fadeCompletePause, howlerId);
                this.soundSprite.off("fade", this.fadeCompleteStop, howlerId);
                this.soundSprite.volume(1, howlerId);
                this.soundSprite.play(howlerId);
                playingSounds.length = 0;
                playingSounds.push(new PlayingSound(howlerId, Object.assign({},soundPlayConfig)));
                this.pausedSounds.delete(aliasId);
            } else {
                if (!playingSounds.length || !soundPlayConfig.music) {
                    if (playingSounds.length > 0 && soundPlayConfig.singleton) {
                        this.stopSound(aliasId);
                        playingSounds = this.playingSounds.get(aliasId);
                    }

                    // Create new sound instance
                    howlerId = this.soundSprite.play(soundId);
                    this.soundSprite.mute(this.getMutedForSound(soundPlayConfig), howlerId);
                    this.soundSprite.loop(soundPlayConfig.loop, howlerId);
                    playingSounds.push(new PlayingSound(howlerId, Object.assign({},soundPlayConfig)));
                }
            }
        } catch (e) {
            throw new Error("Error trying to play sound " + aliasId + "(" + soundPlayConfig.id + ")");
        }

        this.updateMute();

        for (const playingSound of playingSounds) {
            if (soundPlayConfig.fadeTime > 0) {
                this.soundSprite.fade(playingSound.config.volume, soundPlayConfig.volume, soundPlayConfig.fadeTime, playingSound.howlerId);
            } else {
                this.soundSprite.volume(soundPlayConfig.volume, playingSound.howlerId);
            }
            playingSound.config.volume = soundPlayConfig.volume;
        }

        if (!playingSounds && soundPlayConfig.fadeTime > 0) {
            this.soundSprite.fade(0, soundPlayConfig.volume, soundPlayConfig.fadeTime, howlerId);
        }

        return howlerId;
    }

    private updateMute() {
        this.playingSounds.forEach((sounds: PlayingSound[]) => {
            for (const sound of sounds) {
                const howlerId = sound.howlerId;

                this.soundSprite.mute(this.getMutedForSound(sound.config), howlerId);
            }
        });
    }

    private stopSound(aliasId: string, fadeTime: number = 0, pause: boolean = false): boolean {
        let soundsToStop = this.playingSounds.get(aliasId);

        if (this.soundsWaitingForLoad.has(aliasId)) {
            this.soundsWaitingForLoad.delete(aliasId);
        }

        if (soundsToStop && soundsToStop.length > 0) {
            soundsToStop = [...soundsToStop];
            this.playingSounds.set(aliasId, []);

            for (const sound of soundsToStop) {

                if (sound.config.music) {
                    const musicIndex = this.musicQueue.indexOf(aliasId);
                    if (musicIndex !== -1) {
                        this.musicQueue.splice(musicIndex, 1);
                    }
                }

                if (pause) {
                    this.pausedSounds.set(aliasId, sound.howlerId);
                //    this.playingSounds.get(aliasId).push(new PlayingSound(sound.howlerId, sound.config));
                }

                const completeCallback = pause ? this.fadeCompletePause : this.fadeCompleteStop;

                if (fadeTime === 0) {
                    completeCallback(sound.howlerId);
                } else {
                    this.soundSprite.fade(1, 0, fadeTime, sound.howlerId);
                    this.soundSprite.once("fade", completeCallback, sound.howlerId);
                }
            }

            this.updateMute();

            return true;
        } else {
            return false;
        }
    }

    private fadeCompletePause = (howlerId: number) => {
        this.soundSprite.pause(howlerId);
        this.updateMute();
    }

    private fadeCompleteStop = (howlerId: number) => {
        this.soundSprite.stop(howlerId);
        this.updateMute();
    }

    private loadSettings() {
        if (ls) {
            const soundFxMuted = ls.get("soundFxMuted");
            const musicMuted = ls.get("musicMuted");
            const muted = ls.get("muted");
            const volume = ls.get("volume");

            if (soundFxMuted !== null) {
                this.setSoundFxMute(soundFxMuted);
            }

            if (musicMuted !== null) {
                this.setMusicMute(musicMuted);
            }

            if (volume !== null) {
                this.setVolume(volume);
            }

            if (muted !== null) {
                this.setMute(muted);
            } else {
                this.setMute(!DeviceInfo.isDesktop);
            }
        }
    }
}

class SoundPlayConfig {
    constructor(public id: string, public loop: boolean, public fadeTime: number = 0, public singleton: boolean = false, public music: boolean = false, public volume: number = 1) {
    }
}

class SoundStopConfig {
    constructor(public id: string, public fadeTime: number = 0, public pause: boolean = false) {
    }
}

class PlayingSound {
    constructor(public howlerId: number, public config: SoundPlayConfig) {
    }
}

export default new SoundManager();



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./~/slotworks/sound/sound-manager.ts